import { useEffect, useState } from "react";
import { X, MessageCircle, Phone, Calendar, MapPin, UserCheck, ExternalLink } from "lucide-react";
import PageHero from "../components/PageHero";
import { fetchMasterclass } from "../lib/api";
import { SCHOOL } from "../lib/site";

const BROCHURE = [
  { id: "copertina", titolo: "Trinity Masterclass 2026-2027 · Locandina", url: "https://customer-assets-0z36b82j.emergentagent.net/job_music-academy-portal-2/artifacts/2cosxblr_Copertina%20%28fronte%29.webp" },
  { id: "docenti", titolo: "Trinity Masterclass 2026-2027 · Docenti e contatti", url: "https://customer-assets-0z36b82j.emergentagent.net/job_music-academy-portal-2/artifacts/abdtabap_Retro%20sinistro.webp" },
  { id: "chi-siamo", titolo: "Trinity Masterclass 2026-2027 · Chi siamo", url: "https://customer-assets-0z36b82j.emergentagent.net/job_music-academy-portal-2/artifacts/kqlavh0q_Retro%20centro.webp" },
];

export default function Masterclass() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetchMasterclass().then(setItems).catch(() => {});
  }, []);

  const waLink = `https://api.whatsapp.com/send?phone=${SCHOOL.whatsapp.number.replace("+", "")}&text=${encodeURIComponent("Salve, vorrei informazioni sulle masterclass e seminari")}`;

  return (
    <div data-testid="page-masterclass">
      <PageHero
        eyebrow="Approfondimenti · Formazione continua"
        title="Masterclass"
        italic="e seminari."
        subtitle="Incontri di alto livello con concertisti, didatti e ospiti speciali. Aperti agli allievi della scuola e al pubblico esterno."
      />

      <section className="py-20 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
        {/* Masterclass 2026-2027 */}
        <div
          data-testid="masterclass-2026-2027-banner"
          className="grid grid-cols-1 md:grid-cols-12 gap-8 bg-[#A3133A] grain text-[#FBFBF9] p-8 md:p-10 mb-16 items-center"
        >
          <button
            onClick={() => setSelected({ poster: BROCHURE[0].url, titolo: BROCHURE[0].titolo })}
            data-testid="masterclass-brochure-cover"
            className="md:col-span-3 block w-full max-w-[260px] mx-auto md:mx-0 border border-white/25 hover:border-white/70 transition overflow-hidden"
          >
            <img src={BROCHURE[0].url} alt="Locandina Trinity Masterclass 2026-2027" className="w-full h-auto" />
          </button>
          <div className="md:col-span-9">
            <div className="text-[11px] tracking-eyebrow opacity-80 mb-3">Nuova stagione · Novembre 2026 — Giugno 2027</div>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight mb-3">
              Trinity Masterclass <em className="italic font-light">2026 — 2027</em>
            </h2>
            <p className="opacity-90 leading-relaxed max-w-2xl text-sm md:text-base mb-5">
              Sei masterclass con professionisti del panorama musicale internazionale: clarinetto,
              clarinetto piccolo e basso, corno di bassetto, violino, musica da camera e canto lirico.
              Sedi: Trinity Musical School (Rovigo) e Salotto Musicale &ldquo;R. Guiso&rdquo; (Polesella).
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <a
                href="https://www.trinitymusicalschool-masterclass.com"
                target="_blank"
                rel="noreferrer"
                data-testid="masterclass-site-cta"
                className="border border-[#FBFBF9]/60 px-6 py-3 text-[12px] tracking-eyebrow rounded-sm inline-flex items-center gap-2 hover:bg-white hover:text-[#A3133A] transition-all"
              >
                Vai al sito masterclass <ExternalLink size={14} />
              </a>
              <div className="flex gap-3">
                {BROCHURE.slice(1).map((p) => (
                  <button
                    key={p.url}
                    onClick={() => setSelected({ poster: p.url, titolo: p.titolo })}
                    data-testid={`masterclass-brochure-${p.id}`}
                    className="w-16 border border-white/25 hover:border-white/70 transition overflow-hidden"
                  >
                    <img src={p.url} alt={p.titolo} className="w-full h-auto" />
                  </button>
                ))}
              </div>
              <span className="text-[11px] tracking-eyebrow opacity-70">Clicca la locandina per ingrandirla</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 mb-20">
          {items.map((m) => (
            <article key={m.id} data-testid={`masterclass-${m.id}`} className="group flex flex-col">
              {m.poster ? (
                <button
                  onClick={() => setSelected(m)}
                  data-testid={`masterclass-poster-${m.id}`}
                  className="block w-full aspect-[707/1000] overflow-hidden bg-[#A3133A]/5 border border-[#A3133A]/15 hover:border-[#A3133A]/40 transition relative"
                >
                  <img
                    src={m.poster}
                    alt={m.titolo}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-[#A3133A]/0 group-hover:bg-[#A3133A]/30 transition" />
                </button>
              ) : (
                <div
                  data-testid={`masterclass-poster-${m.id}`}
                  className="w-full aspect-[707/1000] bg-[#A3133A] grain border border-[#A3133A]/15 flex items-center justify-center p-8"
                >
                  <div className="text-center text-[#FBFBF9]">
                    <div className="text-[10px] tracking-eyebrow opacity-80 mb-4">Masterclass</div>
                    <div className="font-serif-display text-2xl leading-tight mb-4">{m.titolo}</div>
                    <div className="w-10 h-px bg-white/40 mx-auto mb-4" />
                    {m.docente && <div className="font-serif-display italic text-lg opacity-90">{m.docente}</div>}
                    {m.data && <div className="text-[11px] tracking-eyebrow opacity-75 mt-3">{m.data}</div>}
                  </div>
                </div>
              )}
              <div className="mt-6 flex-1 flex flex-col">
                <h3 className="font-serif-display text-2xl text-[#1C1C1C] mb-3 leading-tight">{m.titolo}</h3>
                <div className="space-y-2 text-[12px] text-[#5C5C5C] mb-4">
                  {m.docente && (
                    <div className="flex items-start gap-2">
                      <UserCheck size={12} className="mt-1 text-[#A3133A] shrink-0" />
                      <span>{m.docente}</span>
                    </div>
                  )}
                  {m.data && (
                    <div className="flex items-start gap-2">
                      <Calendar size={12} className="mt-1 text-[#A3133A] shrink-0" />
                      <span>{m.data}</span>
                    </div>
                  )}
                  {m.luogo && (
                    <div className="flex items-start gap-2">
                      <MapPin size={12} className="mt-1 text-[#A3133A] shrink-0" />
                      <span>{m.luogo}</span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-[#5C5C5C] leading-relaxed">{m.descrizione}</p>
              </div>
            </article>
          ))}
        </div>

        {/* CTA */}
        <div className="bg-[#A3133A] text-[#FBFBF9] p-10 md:p-16 relative overflow-hidden grain rounded-sm text-center">
          <div className="relative z-10">
            <div className="text-[11px] tracking-eyebrow opacity-80 mb-3">Informazioni e iscrizioni</div>
            <h3 className="font-serif-display text-4xl sm:text-5xl mb-5 leading-tight">
              Per dettagli su date e <em className="italic font-light">programma</em>.
            </h3>
            <p className="opacity-90 max-w-2xl mx-auto mb-8 leading-relaxed">
              Per informazioni relative alle varie masterclass e seminari, contattaci via WhatsApp
              o telefono. Ti forniremo il programma completo, i costi di partecipazione e le
              modalità di iscrizione.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                data-testid="masterclass-cta-whatsapp"
                className="bg-[#25D366] text-white px-8 py-4 text-[12px] tracking-eyebrow rounded-sm hover:bg-[#1FAE54] transition inline-flex items-center gap-2"
              >
                <MessageCircle size={14} /> Scrivi su WhatsApp
              </a>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/15 max-w-3xl mx-auto">
              {SCHOOL.phones.map((p, i) => (
                <a
                  key={p.value}
                  href={`tel:${p.tel}`}
                  data-testid={`masterclass-phone-${i}`}
                  className="bg-[#A3133A] py-4 px-2 text-center hover:bg-[#8B0F2F] transition"
                >
                  <Phone size={14} className="mx-auto mb-2 opacity-90" />
                  <div className="text-[10px] tracking-eyebrow opacity-80 mb-1">{p.label}</div>
                  <div className="text-sm font-medium">{p.value}</div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="masterclass-lightbox"
        >
          <button
            onClick={() => setSelected(null)}
            className="absolute top-6 right-6 text-white p-2"
            data-testid="masterclass-lightbox-close"
            aria-label="Chiudi"
          >
            <X size={24} />
          </button>
          <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={selected.poster} alt={selected.titolo} className="w-full h-auto max-h-[85vh] object-contain" />
            <div className="text-center text-white/90 font-serif-display text-2xl mt-4">{selected.titolo}</div>
          </div>
        </div>
      )}
    </div>
  );
}
